<script>
    import { apiUrl } from "@/utils";
    import { sendAnalytics } from "@/core/SendAnalytics";
    import ProductCardButtons from "@/components/ProductCardButtons";
    import ProductPrice from "@igarden/components/ProductPrice";
    import ProductCardIcons from "@igarden/components/ProductCardIcons";

    export default {
        name: "ProductCard",
        components: {
            ProductCardIcons,
            ProductPrice,
            ProductCardButtons,
        },
        props: {
            product: Object,
            position: Number,
            isSmall: Boolean,
            inCartList: Array,
            isOdd: Boolean,
        },
        data() {
            let isPreOrder = false;
            let currentOffers = this.product.offers.filter((offer) => offer.available);

            if (currentOffers.length === 0) {
                // проверка на предзаказ
                currentOffers = this.product.offers.filter((offer) => !offer.available);
                isPreOrder = true;
            }

            const previewImage =
                currentOffers[0].previewImage?.medium || this.product.previewImage?.medium;

            return {
                currentOffers,
                offer: currentOffers[0],
                offerId: currentOffers[0].id,
                previewImage,
                hoverImage: this.product.additionalPreviewImage?.medium,
                price: currentOffers[0].price,
                discountPrice: currentOffers[0].discountPrice,
                discountPercent: currentOffers[0].discountPercent,
                inCart: this.product.inCart || false,
                isPreOrder,
                previewImageFromOffer: "",
            };
        },
        watch: {
            inCartList() {
                this.checkProductInCart();
            },
            offerId() {
                this.checkProductInCart();
            },
        },
        computed: {
            nameProduct() {
                return this.product.shortName ? this.product.shortName : this.product.name;
            },
            sizeButton() {
                return this.isSmall ? "sm" : "md";
            },
            hasIcons() {
                return Object.values(this.product.labels).length > 0;
            },
            productPath() {
                return {
                    name: "Product",
                    params: { slug: this.product.slug },
                };
            },
        },
        mounted() {
            this.checkProductInCart();
        },
        methods: {
            apiUrl,
            checkProductInCart() {
                this.inCartList
                    ? (this.inCart = this.inCartList.includes(this.offerId.toString()))
                    : (this.inCart = false);
            },
            sendDataLayerEvent() {
                sendAnalytics.send(sendAnalytics.events.productClick, {
                    name: this.product.name,
                    offerId: this.offerId,
                    price: this.discountPrice,
                    position: this.position,
                });
            },
        },
    };
</script>

<template>
    <div class="product-card" :class="{ 'product-card--sm': isSmall }">
        <div
            v-if="hasIcons"
            class="product-card__icons"
            :class="{ 'desktop-hidden': !isSmall }"
        >
            <product-card-icons
                :icons="product.labels"
                :is-odd="isOdd"
                :is-small="true"
            ></product-card-icons>
        </div>
        <div class="product-card__header">
            <div class="product-card__img">
                <router-link
                    :to="productPath"
                    class="product-card__img-link js-lazy-load"
                    tabindex="-1"
                    @click.native.prevent="sendDataLayerEvent"
                >
                    <img class="product-card__img-product" :data-src="apiUrl(previewImage)" />
                    <img class="product-card__img-hover" :data-src="apiUrl(hoverImage)" />

                    <div class="product-card__preloader">
                        <preloader :show="true"></preloader>
                    </div>
                </router-link>
            </div>
        </div>

        <div class="product-card__footer">
            <router-link
                :to="productPath"
                class="product-card__wrap"
                @click.native.prevent="sendDataLayerEvent"
            >
                <div class="product-card__name">
                    {{ nameProduct }}
                </div>

                <div class="product-card__price">
                    <product-price
                        :price="discountPrice"
                        :old-price="price"
                        :discount-count="discountPercent"
                        is-card-size
                        :is-small-size="isSmall"
                    ></product-price>
                    <div v-if="hasIcons && !isSmall" class="product-card__icons mobile-hidden">
                        <product-card-icons :icons="product.labels"></product-card-icons>
                    </div>
                </div>
            </router-link>
            <div class="product-card__buttons">
                <product-card-buttons
                    :offer-id="offerId"
                    :product="product"
                    :is-pre-order="isPreOrder"
                    :in-cart="inCart"
                    :discount-price="discountPrice"
                    :size-button="sizeButton"
                    variant-buy-button="v4"
                    initial-text="В корзину"
                    @on-added-to-cart="inCart = true"
                ></product-card-buttons>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
    .product-card {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
        background-color: #fff;
        border-radius: 12px;
        box-shadow: 0 0 10px #f2f2f5;
        transition: 0.3s ease-in-out all;
        &.swiper-slide {
            height: auto;
        }
        &:hover {
            box-shadow: 0 0 0 2px #d3ede0;
            .product-card__img-product {
                opacity: 0;
            }
            .product-card__img-hover {
                opacity: 1;
            }
        }
        @include bp($bp-desktop-sm) {
            border-radius: var(--border-xl);
            box-shadow: 0 0 25px #f2f2f5;
        }
    }

    .product-card__header {
        position: relative;
        padding: 4px 4px 8px;
        @include bp($bp-desktop-sm) {
            padding: 12px 12px 10px;
        }
    }

    .product-card__icons {
        position: absolute;
        z-index: 10;
        top: 3px;
        right: 3px;
        transform: translateZ(0);
        @include bp($bp-desktop-sm) {
            position: relative;
            top: auto;
            bottom: -4px;
        }
    }

    .product-card__img {
        position: relative;
        background-color: var(--primary);
        border-radius: 8px;
        overflow: hidden;
    }

    .product-card__img-link {
        position: relative;
        display: block;
        padding-bottom: 100%;
        overflow: hidden;

        img {
            @include center-absolute(0);
            margin: auto;
            max-height: 100%;
            border-radius: 8px;
        }
        @include bp($bp-desktop-sm) {
            border-radius: var(--border-md);
            img {
                border-radius: var(--border-md);
            }
        }
    }

    .product-card__preloader {
        opacity: 1;
    }

    .product-card__img-product {
        z-index: 1;
        display: none;
        transform: scale(0.85);
        transform-origin: bottom;
        transition: opacity 0.3s ease-in;

        animation: fadeInMobile 0.3s ease-in-out;

        @include bp($bp-desktop-sm) {
            transform: none;
            animation-name: fadeIn;
        }

        @keyframes fadeInMobile {
            from {
                opacity: 0;
                transform: scale(0.8);
            }
            to {
                opacity: 1;
                transform: scale(0.85);
            }
        }

        @keyframes fadeIn {
            from {
                opacity: 0;
                transform: scale(0.9);
            }
            to {
                opacity: 1;
                transform: scale(1);
            }
        }

        &[lazy="loaded"] {
            display: block;
            & ~ .product-card__preloader {
                opacity: 0;
            }
        }
    }

    .product-card__img-hover {
        z-index: 3;
        opacity: 0;
        transition: opacity 0.3s ease-in;
    }

    .product-card__footer {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        padding: 0 8px 8px;
        @include bp($bp-desktop-sm) {
            padding: 0 16px 20px;
        }
    }

    .product-card__buttons {
        width: 100%;
    }

    .product-card__wrap {
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        padding: 0 4px;
        @include bp($bp-desktop-sm) {
            padding: 0 8px;
        }
    }

    .product-card__name {
        position: relative;
        z-index: 2;
        display: block;
        flex-grow: 1;
        margin-bottom: 8px;
        color: var(--primary-text);
        font-family: var(--secondary-font);
        font-size: 12px;
        line-height: 16px;
        font-weight: 400;
        letter-spacing: -0.02em;
        transition: 0.3s ease-in-out all;
        @include bp($bp-desktop-sm) {
            margin-bottom: 16px;
            font-size: 16px;
            line-height: 20px;
            letter-spacing: 0;
        }
    }

    .product-card__price {
        display: flex;
        justify-content: space-between;
        margin-bottom: 10px;
        @include bp($bp-desktop-sm) {
            margin-bottom: 16px;
            min-height: 40px;
        }
    }

    //
    // --mod
    //
    .product-card--sm {
        box-shadow: 0 0 10px #f2f2f5;
        border-radius: 12px;
        @include bp($bp-desktop-sm) {
            border-radius: var(--border-md);
        }
        .product-card__icons {
            position: absolute;
            z-index: 20;
            top: 3px;
            right: 3px;
            bottom: auto;
        }

        .product-card__header {
            padding: 4px;
            @include bp($bp-desktop-sm) {
                padding: 8px;
            }
        }

        .product-card__name {
            margin-bottom: 8px;
            font-size: 12px;
            line-height: 16px;
            @include bp($bp-desktop-sm) {
                margin-bottom: 32px;
                font-size: 14px;
                line-height: 20px;
            }
        }

        .product-card__img-link {
            padding-bottom: 100%;
        }

        .product-card__price {
            margin-bottom: 8px;
        }

        .product-card__wrap {
            padding: 0;
        }

        .product-card__controls {
            max-width: 100%;
            padding: 0;
        }

        .product-card__footer {
            padding: 0 8px 8px;
            @include bp($bp-desktop-sm) {
                padding: 0 12px 12px;
            }
        }
    }
</style>
